/**
 * i18n core: the English dictionary, plural entries, and date and number
 * formatting through Intl. The app's interface is English only (owner
 * decision 2026-10-29): one UI language, Latin digits, the Gregorian
 * calendar. What people write (names, messages, topics) can be in any
 * script; it is never translated and keeps its own direction (`<bdi>`,
 * `dir="auto"`).
 *
 * Strings come from two places:
 * - `proto.generated.ts`: the prototype's strings this client uses
 *   (`node scripts/sync-proto-strings.mjs` regenerates it);
 * - `en.ts` and the area tables: strings the real client adds.
 * A test checks that every key used in `src/` exists.
 */
import { createElement, type ReactNode } from 'react';

export type Lang = 'en';
export type Plural = { one: string; other: string };
export type Entry = string | Plural;
export type Dict = Record<string, Entry>;

export type Vars = Record<string, string | number | null | undefined>;
/** Values for tRich(): text, numbers or nodes. */
export type RichVars = Record<string, ReactNode>;

const TZ_DEFAULT = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
/** The one UI locale (Intl). */
export const UI_LOCALE = 'en-US';

export interface Translator {
  lang: Lang;
  t(key: string, vars?: Vars): string;
  /**
   * Like t(), but returns nodes with every value in a <bdi> (§8.5): the
   * sentence keeps the UI's direction and a name keeps its own. A value may
   * be a node (a link, a mention chip); numbers are formatted.
   */
  tRich(key: string, vars?: RichVars): ReactNode[];
  has(key: string): boolean;
  fmt: Formatters;
}

export interface Formatters {
  num(n: number, opts?: Intl.NumberFormatOptions): string;
  time(d: Date | string | number): string;
  date(
    d: Date | string | number,
    style?: 'long' | 'short' | 'weekday' | 'monthDay' | 'monthYear',
  ): string;
  day(d: Date | string | number, now?: Date): string;
  stamp(d: Date | string | number, now?: Date): string;
  dayKey(d: Date | string | number): string;
  dayDiff(d: Date | string | number, now?: Date): number;
  rel(d: Date | string | number, now?: Date): string;
  dur(sec: number): string;
  ms(n: number): string;
}

function toDate(d: Date | string | number): Date {
  return d instanceof Date ? d : new Date(d);
}

export function createFormatters(_lang: Lang = 'en', timeZone: string = TZ_DEFAULT): Formatters {
  const locale = UI_LOCALE;
  const cache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>();
  const dtf = (key: string, opts: Intl.DateTimeFormatOptions) => {
    let f = cache.get(key) as Intl.DateTimeFormat | undefined;
    if (!f) {
      f = new Intl.DateTimeFormat(locale, { timeZone, ...opts });
      cache.set(key, f);
    }
    return f;
  };
  const keyFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const f: Formatters = {
    num(n, opts) {
      if (opts) return new Intl.NumberFormat(locale, opts).format(n);
      let nf = cache.get('num') as Intl.NumberFormat | undefined;
      if (!nf) {
        nf = new Intl.NumberFormat(locale);
        cache.set('num', nf);
      }
      return nf.format(n);
    },
    time(d) {
      return dtf('time', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
        toDate(d),
      );
    },
    date(d, style = 'long') {
      const date = toDate(d);
      switch (style) {
        case 'short':
          return dtf('date-s', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
        case 'weekday':
          return dtf('date-w', { weekday: 'long' }).format(date);
        case 'monthDay':
          return dtf('date-md', { month: 'long', day: 'numeric' }).format(date);
        case 'monthYear':
          return dtf('date-my', { month: 'long', year: 'numeric' }).format(date);
        default:
          return dtf('date-l', { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
      }
    },
    dayKey(d) {
      return keyFmt.format(toDate(d));
    },
    dayDiff(d, now = new Date()) {
      const a = Date.parse(keyFmt.format(toDate(d)));
      const b = Date.parse(keyFmt.format(now));
      return Math.round((a - b) / 86_400_000);
    },
    day(d, now) {
      const diff = f.dayDiff(d, now);
      if (diff === 0) return 'Today';
      if (diff === -1) return 'Yesterday';
      return f.date(d);
    },
    stamp(d, now) {
      const diff = f.dayDiff(d, now);
      const time = f.time(d);
      if (diff === 0) return `Today at ${time}`;
      if (diff === -1) return `Yesterday at ${time}`;
      return `${f.date(d, 'short')} ${time}`;
    },
    rel(d, now = new Date()) {
      const sec = Math.round((toDate(d).getTime() - now.getTime()) / 1000);
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      const abs = Math.abs(sec);
      if (abs < 60) return rtf.format(sec, 'second');
      if (abs < 3600) return rtf.format(Math.round(sec / 60), 'minute');
      if (abs < 86400) return rtf.format(Math.round(sec / 3600), 'hour');
      return rtf.format(Math.round(sec / 86400), 'day');
    },
    dur(sec) {
      const s = Math.max(0, Math.round(sec));
      const d = Math.floor(s / 86400);
      const h = Math.floor((s % 86400) / 3600);
      const m = Math.floor((s % 3600) / 60);
      if (d) return `${d}d ${h}h`;
      if (h) return `${h}h ${m}m`;
      if (m) return `${m}m`;
      return `${s}s`;
    },
    ms(n) {
      // latency stays Latin (design-system 9): callers wrap it in .nx-ltr
      return `${Math.max(1, Math.round(n))} ms`;
    },
  };
  return f;
}

/** A list in running text: "Ali, Sara, and Kian". */
export function listFormat(items: string[]): string {
  return new Intl.ListFormat(UI_LOCALE, { type: 'conjunction' }).format(items);
}

/** U+2068 FIRST STRONG ISOLATE and U+2069 POP DIRECTIONAL ISOLATE. */
export const FSI = '⁨';
export const PDI = '⁩';

/**
 * Text without the direction isolates `interpolate` adds (U+2066-U+2069):
 * for a translated string that is stored or searched, not only shown.
 */
export function plainText(s: string): string {
  return s.replace(/[⁦-⁩]/g, '');
}

/**
 * Fills `{name}` placeholders; numbers are formatted.
 * String values (names, channel titles: user text) are direction-isolated,
 * the text equivalent of `<bdi>`: a right-to-left name inside an
 * English sentence keeps its own order and does not reorder the words
 * around it.
 */
export function interpolate(template: string, vars: Vars | undefined, fmt: Formatters): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, k: string) => {
    const v = vars[k];
    if (v === undefined || v === null) return m;
    return typeof v === 'number' ? fmt.num(v) : `${FSI}${v}${PDI}`;
  });
}

/**
 * Fills `{name}` placeholders with nodes: each value in a <bdi> (the element
 * form of the isolates interpolate() adds), numbers formatted. Plain text
 * between values stays text.
 */
export function richText(
  template: string,
  vars: RichVars | undefined,
  fmt: Formatters,
): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of template.matchAll(/\{(\w+)\}/g)) {
    const v = vars?.[m[1]!];
    if (v === undefined || v === null) continue;
    if (m.index! > last) out.push(template.slice(last, m.index));
    out.push(createElement('bdi', { key: i++ }, typeof v === 'number' ? fmt.num(v) : v));
    last = m.index! + m[0].length;
  }
  if (last < template.length) out.push(template.slice(last));
  return out;
}

/**
 * A phrase worded to sit inside a sentence ("Couldn't join Lobby: a
 * moderator disconnected you"), standing alone as a line of its own: its
 * first letter in upper case (QA 2026-10-28 follow-up round 2). A name
 * first is unchanged (it starts with an isolate, and a name is written as
 * its owner wrote it).
 */
export function asSentence(s: string): string {
  return s.replace(/^\p{Ll}/u, (c) => c.toLocaleUpperCase(UI_LOCALE));
}

export function createTranslator(
  _lang: Lang,
  dicts: Record<Lang, Dict>,
  timeZone?: string,
): Translator {
  const fmt = createFormatters('en', timeZone);
  const dict = dicts.en;
  const pr = new Intl.PluralRules(UI_LOCALE);
  const pick = (key: string, n: unknown): string | undefined => {
    const e = dict[key];
    if (e === undefined) return undefined;
    if (typeof e === 'string') return e;
    return pr.select(typeof n === 'number' ? n : 0) === 'one' ? e.one : e.other;
  };
  return {
    lang: 'en',
    fmt,
    has: (key) => key in dict,
    t(key, vars) {
      const s = pick(key, vars?.n);
      return s === undefined ? key : interpolate(s, vars, fmt);
    },
    tRich(key, vars) {
      const s = pick(key, vars?.n);
      return s === undefined ? [key] : richText(s, vars, fmt);
    },
  };
}
